/* eslint-disable max-lines */
import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  AccountProductEnum,
  AnalyticsEvent,
  ButtonComponentActionResultTypenames,
  ButtonComponentV2,
  CompleteTabapayAuthTransferInput,
  CompleteTabapayAuthTransferResultTypenames,
  CreateTabapayAuthTransferInput,
  CreateTabapayAuthTransferResultTypenames,
  DebitCardFundingLimitOptionsResultTypenames,
  ExternalCardStatus,
  FundingFlowInput,
  InternalCheckingAccount,
  InternalSavingsAccount,
  ProcessTypeInput,
  Typenames,
} from '@varo/graphql-schema-types';
import {
  Href,
  useFocusEffect,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import useCancelTabapayTransfer from 'features/moveMoney/screens/dcf/hooks/useCancelTabapayTransfer';
import useCompleteTabapayTransferAuth from 'features/moveMoney/screens/dcf/hooks/useCompleteTabapayTransferAuth';
import { useDebitCardOptions } from 'features/moveMoney/screens/dcf/hooks/useDebitCardOptions';
import { useLogGQLErrorScreenAnalytics } from 'features/moveMoney/screens/dcf/hooks/useLogGQLErrorScreenAnalytics';
import { useThreeDsHandler } from 'features/moveMoney/screens/dcf/hooks/useThreeDsHandler';
import useThreeDsSdkWeb from 'features/moveMoney/screens/dcf/hooks/useThreeDsSdkWeb';
import {
  getFlowNameForAnalytics,
  getScreenName,
  handleActionRoute,
  TabaPayEntryFlowType,
} from 'features/moveMoney/screens/dcf/tabapay/helpers/TabaPay.helpers';
import { useTabaPayAddress } from 'features/moveMoney/screens/dcf/tabapay/providers/TabaPayContextProvider';
import useCreateTabapayAuth from 'features/myVaro/screens/ExternalAccounts/hooks/useCreateTabapayAuth';
import useDeleteExternalCard from 'features/myVaro/screens/ExternalAccounts/hooks/useDeleteExternalCard';
import useExternalsCards from 'features/myVaro/screens/ExternalAccounts/hooks/useExternalsCards';
import { useCallback, useEffect, useRef, useState } from 'react';
import { parseGQLAnalyticsEvent } from 'services/analytics/helpers/parseGQLAnalyticsEvent';
import myAccountsQuery from 'services/graphql/queries/MoveMoney/GetMyAccounts';
import { isWeb } from 'shared/constants/platform';
import { navigateBack } from 'shared/helpers/backNavigation.helpers';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { HomeTileTypeIds } from 'shared/hooks/homeTiles/HomeTileTypeIds';
import useRefreshHomeTiles from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import { ErrorScreenType } from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import useVaroOnboardingFlow from 'shared/utils/useVaroOnboardingFlow/useVaroOnboardingFlow';
import { useQuery } from 'urql';

import useTabaPayDebitCardFundingLimitOptions from './useTabapayDebitCardFundingLimitOptions';

interface MyAccountsResponse {
  myAccounts: {
    internal: (InternalCheckingAccount | InternalSavingsAccount)[];
  };
  fetching: boolean;
  error: {
    message: string;
  };
}

enum FailurePoint {
  CREATE_AUTH = 'createTabapayTransactionAuth',
  THREE_DS = 'threeDs',
  COMPLETE_AUTH = 'completeTabapayTransactionAuth',
  PARTIAL_AUTH = 'partialAuth',
}

export interface UseTabapayTransferOptions {
  onPartialAuthCancelSuccess?: (approvedAmount?: string) => void;
}

// Survives router.replace remounts so first and subsequent X dismissals differ.
// The first dismissal resets to 0.00; later dismissals carry the approved amount.
// Reset on fresh screen entry by the effect below.
let hasDismissedPartialAuth = false;

const useTabapayTransfer = (options?: UseTabapayTransferOptions) => {
  const router = useRouter();
  const [transactionId, setTransactionId] = useState('');
  const [failurePoint, setFailurePoint] = useState<FailurePoint>();
  const bottomSheetRef = useRef<BottomSheetModal>(null);
  const [ignoreOnChange, setIgnoreOnChange] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);
  const [shouldDeleteCard, setShouldDeleteCard] = useState(false);
  const [actionRoute, setActionRoute] = useState('');
  const cancelSuccessHandlerRef = useRef<() => void>(() => {});
  const isErrorActionInFlight = useRef(false);
  const isPartialAuthCloseRef = useRef(false);
  const onPartialAuthCancelSuccessRef = useRef<
    ((approvedAmount?: string) => void) | undefined
  >(undefined);

  // Updated synchronously so the latest callback is always available in async
  // callbacks. Safe because refs never trigger re-renders and this value is
  // only read from async callbacks, never during render.
  onPartialAuthCancelSuccessRef.current = options?.onPartialAuthCancelSuccess;
  const { isBelieveDay0Flow, believeCardId, partialAuthAmount } =
    useLocalSearchParams<{
      isBelieveDay0Flow?: string;
      believeCardId?: string;
      // Backend-approved partial-auth amount carried from V2 on close, so this
      // screen starts with that amount instead of defaulting to 0.00.
      partialAuthAmount?: string;
    }>();
  const [transferAmount, setTransferAmount] = useState<string>(
    partialAuthAmount ?? '0.00',
  );
  const [{ data: accountData, fetching }] = useQuery<MyAccountsResponse>({
    query: myAccountsQuery,
  });

  const {
    isEligible,
    errorScreen: eligibleErrorScreen,
    fetching: eligibleFetching,
    minCardDebitFundingAmount,
    maxCardDebitFundingAmount,
    limitErrorMessage,
    data: tabaPayLimitData,
  } = useTabaPayDebitCardFundingLimitOptions();

  // Kept for its loading state only: limits now come from
  // useTabaPayDebitCardFundingLimitOptions, but this query must still resolve
  // before the transfer form renders, so its loading feeds combinedLoading.
  const { loading: debitCardOptionLoading } = useDebitCardOptions();

  const maxAmount = maxCardDebitFundingAmount;

  const minAmount = minCardDebitFundingAmount;

  const {
    externalCards,
    fetching: isLoading,
    reExecuteQuery: refetchExternalCards,
  } = useExternalsCards('network-only', true);

  const checkingAccount = accountData?.myAccounts.internal.find(
    (account) => account.product === AccountProductEnum.Checking,
  );
  const firstExternalCard =
    externalCards?.__typename === Typenames.ExternalCardsSuccess
      ? externalCards.externalCard?.[0]
      : undefined;

  const prevExternalCardMetaRef = useRef<{
    id?: string;
    status?: ExternalCardStatus;
    typename?: string;
  }>({});

  useEffect(() => {
    const prev = prevExternalCardMetaRef.current;
    const next = {
      id: firstExternalCard?.id,
      status: firstExternalCard?.status,
      typename: externalCards?.__typename,
    };
    const changed =
      prev.id !== next.id ||
      prev.status !== next.status ||
      prev.typename !== next.typename;
    if (changed) {
      prevExternalCardMetaRef.current = next;
    }
  }, [
    externalCards?.__typename,
    firstExternalCard?.id,
    firstExternalCard?.status,
    externalCards,
  ]);

  const cardId = firstExternalCard?.id;

  const latestCardRef = useRef<{ id?: string; status?: ExternalCardStatus }>(
    {},
  );
  const originalCardIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    latestCardRef.current = {
      id: firstExternalCard?.id,
      status: firstExternalCard?.status,
    };
  }, [firstExternalCard?.id, firstExternalCard?.status]);

  const { updatedMessage, setUpdatedMessage } = useTabaPayAddress();

  const flow = useVaroOnboardingFlow();

  const {
    refreshTiles,
    data,
    fetching: homeFetching,
    error: homeError,
  } = useRefreshHomeTiles([
    HomeTileTypeIds.INTERNAL_ACCOUNT_TILE_CHECKING,
    HomeTileTypeIds.ACCOUNT_ACTION_TILE_ADD_MONEY_DDA,
  ]);

  const {
    cancelTabapayTransfer,
    isLoading: cancelTransferLoading,
    errorScreen: cancelTransferErrorScreen,
  } = useCancelTabapayTransfer({
    onSuccess: () => cancelSuccessHandlerRef.current(),
  });

  const {
    createTabapayTransactionAuth,
    isLoading: authLoading,
    errorScreen: errorCreateAuth,
  } = useCreateTabapayAuth();

  const {
    completeTabapayTransactionAuth,
    isCompleteAuthLoading,
    completeAuthErrorScreen,
  } = useCompleteTabapayTransferAuth();

  const {
    handleThreeDS,
    errorScreenThreeDs: errorThreeDsHandler,
    isLoading: native3DsLoading,
    resetError: resetThreeDsHandlerError,
  } = useThreeDsHandler();

  const {
    startThreeDsWebFlow,
    errorScreenThreeDs: errorThreeDsWeb,
    isLoading: web3DsLoading,
    resetThreeDsSession,
  } = useThreeDsSdkWeb();

  const debitCardFundingLimitOptions =
    tabaPayLimitData?.debitCardFundingLimitOptions;

  const tabaPayLimitMessageData =
    debitCardFundingLimitOptions?.__typename ===
    DebitCardFundingLimitOptionsResultTypenames.DebitCardFundingLimitOptionsSuccess
      ? debitCardFundingLimitOptions
      : null;

  const dailyLimitInformation = tabaPayLimitMessageData?.dailyLimitInformation;

  const monthlyLimitInformation =
    tabaPayLimitMessageData?.monthlyLimitInformation;

  const disclaimer = tabaPayLimitMessageData?.disclaimer;

  const {
    deleteExternalCard,
    isSuccess: deletedExternalCard,
    isLoading: removeCardLoading,
    errorModel: deleteExternalCardError,
  } = useDeleteExternalCard();

  const { defaultTitle, defaultMessage } = useDefaultErrorStrings();

  const handleCompleteAuth = async (transactionId: string) => {
    const completeInput: CompleteTabapayAuthTransferInput = {
      transactionId,
      ...(isBelieveDay0Flow === 'true' && {
        fundingFlow: FundingFlowInput.BelieveOnboardFunding,
      }),
    };
    const res = await completeTabapayTransactionAuth(
      completeInput,
      analyticsFlowName,
    );
    if (
      res?.__typename ===
      CompleteTabapayAuthTransferResultTypenames.CompleteTabapayAuthTransferSuccess
    ) {
      // Clear any stale 3DS / partial-auth error state before navigating away
      // so the error screen (and its loading overlay) can't be re-rendered by
      // a race between the mutation settling and the navigation completing.
      const wasPartialAuth = Boolean(
        errorThreeDsHandler?.isPartialAuth || errorThreeDsWeb?.isPartialAuth,
      );
      resetThreeDsHandlerError?.();
      resetThreeDsSession?.();
      router.dismissTo({
        pathname: `/(authenticated)/${flow}/tabapay/tabapay-transfer-success`,
        params: {
          amount: transferAmount,
          isPartialAuth: String(wasPartialAuth),
          isBelieveDay0Flow,
          believeCardId,
        },
      });
    }
  };

  const continue3Ds = async () => {
    const threeDsResult = await processThreeDs(transactionId);
    if (threeDsResult) {
      await handleCompleteAuth(transactionId);
    } else {
      setFailurePoint(FailurePoint.THREE_DS);
    }
  };

  const processThreeDs = async (argTransactionId: string) => {
    const threeDsFlowName =
      isBelieveDay0Flow === 'true'
        ? 'dcf_believe_day_0_application'
        : TabaPayEntryFlowType.DCFFlow;
    let res: boolean | undefined;
    if (isWeb) {
      res = await startThreeDsWebFlow(
        ProcessTypeInput.Transaction,
        threeDsFlowName,
        undefined,
        argTransactionId,
      );
    } else {
      res = await handleThreeDS(
        ProcessTypeInput.Transaction,
        threeDsFlowName,
        undefined,
        argTransactionId,
      );
    }
    return res;
  };

  const handleSubmit = async (externalCardId?: string) => {
    if ((!externalCardId && !firstExternalCard?.id) || !checkingAccount?.id) {
      return;
    }

    const chosenExternalCardId = firstExternalCard?.id || externalCardId || '';
    if (chosenExternalCardId && !originalCardIdRef.current) {
      originalCardIdRef.current = chosenExternalCardId;
    }

    const createInput: CreateTabapayAuthTransferInput = {
      externalSourceCardId: chosenExternalCardId,
      destinationAccountID: checkingAccount?.id,
      amount: {
        amount: transferAmount,
        currency: 'USD',
      },
      isPartialAuth: true,
      ...(isBelieveDay0Flow === 'true' && {
        fundingFlow: FundingFlowInput.BelieveOnboardFunding,
      }),
    };

    const res = await createTabapayTransactionAuth(
      createInput,
      analyticsFlowName,
    );
    if (
      res?.__typename ===
      CreateTabapayAuthTransferResultTypenames.CreateTabapayAuthTransferSuccess
    ) {
      const transactionId = res.transactionId;
      setTransactionId(transactionId);
      const run3ds = res.isChallengeRequired;
      if (run3ds) {
        const result = await processThreeDs(transactionId);
        if (result) {
          await handleCompleteAuth(transactionId);
        } else {
          setFailurePoint(FailurePoint.THREE_DS);
        }
      } else {
        await handleCompleteAuth(transactionId);
      }
    }
  };

  const { segmentClient } = useAnalyticsConfiguration();
  const flowName = getFlowNameForAnalytics(TabaPayEntryFlowType.DCFFlow);
  const analyticsFlowName =
    isBelieveDay0Flow === 'true' ? 'dcf_believe_day_0_application' : flowName;
  const sourceName =
    errorThreeDsHandler?.statusCode || errorThreeDsWeb?.statusCode;

  const screenName = getScreenName(sourceName);
  const logAnalyticsEvent = async (event: AnalyticsEvent, flowName: string) => {
    const { name, properties } = parseGQLAnalyticsEvent(event);
    await segmentClient.logEvent(name, {
      ...properties,
      flow_name: flowName,
    });
  };

  const handleErrorButtonPress = async (button?: ButtonComponentV2) => {
    if (!button?.buttonOnClick || isErrorActionInFlight.current) {
      return;
    }
    isErrorActionInFlight.current = true;

    try {
      const action = button.buttonOnClick;

      switch (action.__typename) {
        case ButtonComponentActionResultTypenames.RouteNavigationAction:
          if (action.analyticsEvent) {
            await logAnalyticsEvent(action.analyticsEvent, flowName);
          }
          setActionRoute(action.route);
          checkAndDeleteCard(action.route);
          break;

        case ButtonComponentActionResultTypenames.SDContinueClientFlowAction:
          if (action.analyticsEvent) {
            await logAnalyticsEvent(action.analyticsEvent, flowName);
          }
          if (failurePoint === FailurePoint.CREATE_AUTH) {
            await handleSubmit();
          } else if (failurePoint === FailurePoint.THREE_DS) {
            await continue3Ds();
          } else if (
            failurePoint === FailurePoint.COMPLETE_AUTH ||
            failurePoint === FailurePoint.PARTIAL_AUTH
          ) {
            await handleCompleteAuth(transactionId);
          }
          break;

        case ButtonComponentActionResultTypenames.SDBackNavigationAction:
          if (action.analyticsEvent) {
            await logAnalyticsEvent(action.analyticsEvent, flowName);
          }
          if (button.id === 'EXIT_BUTTON') {
            if (
              errorThreeDsWeb?.processType === ProcessTypeInput.Transaction ||
              errorThreeDsHandler?.processType === ProcessTypeInput.Transaction
            ) {
              void cancelTabapayTransfer(
                {
                  transactionId,
                },
                analyticsFlowName,
              );
            } else {
              navigateBack();
            }
          } else if (button.id === 'ADD_DIFFERENT_CARD') {
            cancelTransactionAndDeleteCard();
          } else {
            navigateBack();
          }
          break;

        default:
          navigateBack();
      }
    } finally {
      isErrorActionInFlight.current = false;
    }
  };

  let queryErrorScreen: {
    screenType: ErrorScreenType.Error;
    messageHeadline: string;
    messageBody: string;
    primaryButton?: ButtonComponentV2;
    secondaryButton?: ButtonComponentV2;
    is3DSError?: boolean;
    analytics?: AnalyticsEvent;
  } | null = null;
  if (
    !fetching &&
    !isLoading &&
    (!firstExternalCard?.id || !checkingAccount?.id)
  ) {
    queryErrorScreen = {
      screenType: ErrorScreenType.Error,
      messageHeadline: defaultTitle,
      messageBody: defaultMessage,
    };
  }

  const combinedErrorScreen =
    completeAuthErrorScreen ||
    errorCreateAuth ||
    errorThreeDsHandler ||
    errorThreeDsWeb ||
    queryErrorScreen ||
    cancelTransferErrorScreen ||
    deleteExternalCardError;

  const combinedErrorScreen2 =
    completeAuthErrorScreen ||
    errorCreateAuth ||
    errorThreeDsHandler ||
    errorThreeDsWeb ||
    cancelTransferErrorScreen ||
    deleteExternalCardError;

  const combinedLoading =
    fetching ||
    (firstLoad && isLoading) ||
    authLoading ||
    native3DsLoading ||
    web3DsLoading ||
    isCompleteAuthLoading ||
    debitCardOptionLoading ||
    cancelTransferLoading ||
    removeCardLoading ||
    eligibleFetching;

  const isProcessing =
    cancelTransferLoading ||
    removeCardLoading ||
    isCompleteAuthLoading ||
    authLoading;

  const shouldShowBackButton =
    !(isWeb && combinedErrorScreen) &&
    !combinedErrorScreen?.is3DSError &&
    !combinedLoading;

  useLogGQLErrorScreenAnalytics(
    combinedErrorScreen?.analytics,
    screenName,
    flowName,
  );

  const resolveDeletionTarget = useCallback(() => {
    const liveId = firstExternalCard?.id;
    const liveStatus = firstExternalCard?.status;
    const refId = latestCardRef.current.id;
    const candidateId = liveId || refId;
    const fallbackOriginalId = candidateId || originalCardIdRef.current;
    if (liveStatus && liveStatus !== ExternalCardStatus.Active) {
      return { id: candidateId, reason: 'status' as const };
    }
    if (
      !liveStatus &&
      failurePoint === FailurePoint.THREE_DS &&
      fallbackOriginalId
    ) {
      return { id: fallbackOriginalId, reason: 'threeDsFallback' as const };
    }
    return { id: undefined, reason: undefined };
  }, [
    failurePoint,
    firstExternalCard?.id,
    firstExternalCard?.status,
    latestCardRef,
    originalCardIdRef,
  ]);

  const checkAndDeleteCard = useCallback(
    (routeOverride?: string) => {
      const { id: deletionTargetId } = resolveDeletionTarget();
      if (deletionTargetId) {
        void deleteExternalCard({ externalCardId: deletionTargetId });
        return;
      }
      const routeToUse = routeOverride || actionRoute;
      if (routeToUse) {
        const targetRoute =
          `${routeToUse}?tabaPayEntryFlowType=${TabaPayEntryFlowType.DCFFlow}` as Href;
        router.replace(targetRoute);
        return;
      }
      refreshTiles();
    },
    [
      actionRoute,
      deleteExternalCard,
      refreshTiles,
      resolveDeletionTarget,
      router,
    ],
  );

  const cancelTransactionAndDeleteCard = () => {
    if (firstExternalCard?.id) {
      if (transactionId?.trim()) {
        setShouldDeleteCard(true);
        void cancelTabapayTransfer({
          transactionId: transactionId.trim(),
        });
      } else {
        checkAndDeleteCard();
      }
    } else {
      navigateBack();
    }
  };

  useEffect(() => {
    if ((data?.tiles || homeError) && !homeFetching) {
      handleActionRoute(actionRoute, flow, TabaPayEntryFlowType.DCFFlow);
    }
  }, [data, fetching, homeError, homeFetching, flow, actionRoute]);

  // Missing partialAuthAmount identifies a fresh transfer-flow entry.
  // This resets the first/subsequent dismissal state after leaving or switching flows.
  // Same-flow router.replace carries the param and preserves the state.
  useEffect(() => {
    if (!partialAuthAmount) {
      hasDismissedPartialAuth = false;
    }
  }, [partialAuthAmount]);

  useFocusEffect(
    useCallback(() => {
      refetchExternalCards({ requestPolicy: 'network-only' });
      setFirstLoad(false);
    }, [refetchExternalCards]),
  );

  // Keep the cancel-success handler ref in sync so the onSuccess callback
  // always invokes the latest version with fresh closure values.
  useEffect(() => {
    cancelSuccessHandlerRef.current = () => {
      resetThreeDsHandlerError?.();
      resetThreeDsSession?.();

      // When partial auth close triggers cancellation, stay on the transfer
      // screen by skipping refreshTiles / checkAndDeleteCard. If an
      // onPartialAuthCancelSuccess callback was provided (e.g. from the V2
      // new-card flow), invoke it so the caller can navigate appropriately.
      if (isPartialAuthCloseRef.current) {
        isPartialAuthCloseRef.current = false;
        const approvedAmount = partialAuthApprovedAmountRef.current;
        partialAuthApprovedAmountRef.current = undefined;
        const nextAmount = hasDismissedPartialAuth
          ? (approvedAmount ?? '0.00')
          : '0.00';
        hasDismissedPartialAuth = true;
        setTransferAmount(nextAmount);
        onPartialAuthCancelSuccessRef.current?.(nextAmount);
        return;
      }

      if (shouldDeleteCard && firstExternalCard?.id) {
        checkAndDeleteCard();
      } else {
        refreshTiles();
      }
    };
  }, [
    shouldDeleteCard,
    firstExternalCard?.id,
    refreshTiles,
    checkAndDeleteCard,
    resetThreeDsHandlerError,
    resetThreeDsSession,
  ]);

  useEffect(() => {
    if (
      !isLoading &&
      firstExternalCard?.status === ExternalCardStatus.Expired
    ) {
      bottomSheetRef?.current?.present();
    }
  }, [firstExternalCard?.status, isLoading]);

  useEffect(() => {
    if (completeAuthErrorScreen) {
      setFailurePoint(FailurePoint.COMPLETE_AUTH);
    } else if (errorCreateAuth) {
      setFailurePoint(FailurePoint.CREATE_AUTH);
    }
  }, [completeAuthErrorScreen, errorCreateAuth]);

  useEffect(() => {
    if (errorThreeDsWeb?.isPartialAuth && errorThreeDsWeb.approvedAmount) {
      setFailurePoint(FailurePoint.PARTIAL_AUTH);
      setTransferAmount(errorThreeDsWeb.approvedAmount);
    }
    if (
      errorThreeDsHandler?.isPartialAuth &&
      errorThreeDsHandler.approvedAmount
    ) {
      setFailurePoint(FailurePoint.PARTIAL_AUTH);
      setTransferAmount(errorThreeDsHandler.approvedAmount);
    }
  }, [
    errorThreeDsHandler?.approvedAmount,
    errorThreeDsHandler?.isPartialAuth,
    errorThreeDsWeb?.approvedAmount,
    errorThreeDsWeb?.isPartialAuth,
  ]);

  useEffect(() => {
    if (deletedExternalCard) {
      refreshTiles();
    }
  }, [actionRoute, deletedExternalCard, refreshTiles]);

  const partialAuthApprovedAmountRef = useRef<string | undefined>(undefined);

  const markPartialAuthClose = useCallback(() => {
    // Capture at press time so mutable form state can't corrupt the value forwarded to the destination.
    partialAuthApprovedAmountRef.current =
      errorThreeDsWeb?.approvedAmount ?? errorThreeDsHandler?.approvedAmount;
    isPartialAuthCloseRef.current = true;
  }, [errorThreeDsWeb, errorThreeDsHandler]);

  return {
    checkingAccount,
    firstExternalCard,
    transferAmount,
    setTransferAmount,
    handleSubmit,
    bottomSheetRef,
    ignoreOnChange,
    setIgnoreOnChange,
    handleErrorButtonPress,
    cardId,
    combinedErrorScreen,
    combinedLoading,
    updatedMessage,
    setUpdatedMessage,
    shouldShowBackButton,
    cancelTabapayTransfer,
    transactionId,
    errorThreeDsWeb,
    errorThreeDsHandler,
    sourceName: sourceName ? sourceName.toLowerCase() : '',
    checkAndDeleteCard,
    tabaPayLimitMessageData,
    monthlyLimitInformation,
    dailyLimitInformation,
    disclaimer,
    limitErrorMessage,
    maxAmount,
    minAmount,
    isEligible,
    eligibleErrorScreen,
    combinedErrorScreen2,
    isProcessing,
    markPartialAuthClose,
  };
};

export default useTabapayTransfer;
